import {useState, useEffect} from 'react';
import { BUTTON_VARIANTS} from 'cdk-radial';
import {useUnifyAdminStore} from '../../AdminStore';
import {useArchiveCampaign, useCreateCampaign, useUpdateCampaign} from '../CampaignStore/DataQueries/useCampaignsQueries';
import * as StyledComponents from '../StyledComponents'
import {CreateCampaignDialogBody} from './CampaignDialogBody';
import * as AdminStyle from '../../StyledComponents/index';
import { useTranslation } from 'react-i18next';

const CreatePromotionTitle = (): JSX.Element => {
    const { t } = useTranslation();
    const campaignItems = useUnifyAdminStore((state) => state.campaignItems);

    return <StyledComponents.StyledPromoTitle>
        <div>{campaignItems.id ? t('campaigns.updateButton') : t('campaigns.createButton')}</div>
    </StyledComponents.StyledPromoTitle>
}

interface ButtonProps {
    id: string,
    onClick: Function,
    text: string,
    variant: string
}


export default function CreateCampaignDialog({
    model
}:any): JSX.Element {
    const { t } = useTranslation();
    /**
     * Data Store
     */
    const isCreateCampaignDialogOpen = useUnifyAdminStore((state) => state.isCreateCampaignDialogOpen);
    const setIsCreateCampaignDialogOpen = useUnifyAdminStore((state) => state.setIsCreateCampaignDialogOpen);
    const campaignItem = useUnifyAdminStore((state) => state.campaignItems)
    const resetCampaignItem = useUnifyAdminStore((state) => state.setCampaignItem)
    const permissions = useUnifyAdminStore(state => state.permissions) 
    const setShowDialogToast = useUnifyAdminStore((state) => state.setShowDialogToast);
    const showDialogToast = useUnifyAdminStore((state) => state.showDialogToast);
    const setDialogToastContent = useUnifyAdminStore((state) => state.setDialogToastContent);
    const dialogToastContent = useUnifyAdminStore((state) => state.dialogToastContent);
    const archiveSwitch = useUnifyAdminStore((state) => state.campaignArchiveSwitch);

    /**
     * mutations
     */
    const {mutate: createCampaign} = useCreateCampaign()
    const {mutate: updateCampaign} = useUpdateCampaign()
    const {mutate: archiveCampaign} = useArchiveCampaign()

    const handleCreatePromotion = () => {
        if (!campaignItem.name?.enUS) {
            setShowDialogToast(true);
            setDialogToastContent(t('campaigns.toastInvalidName'));
            return;
        }
        const urlRegex = /^(http|https):\/\/[^ "]+$/;
        if(campaignItem.link){
        const isLinkValid = urlRegex.test(campaignItem.link);
        if (!isLinkValid) {
            setShowDialogToast(true);
            setDialogToastContent(t('campaigns.toastInvalidLink'));
            return;
        }}

        if (!campaignItem.description?.enUS) {
            setShowDialogToast(true);
            setDialogToastContent(t('campaigns.toastInvalidDescription'));
            return;
        }
        if (campaignItem.showLogo && !campaignItem.logo && !campaignItem.darkLogo) {
            setShowDialogToast(true);
            setDialogToastContent(t('campaigns.toastInvalidLogo'));
            return;
        }

        const isValid =
            campaignItem?.targetEnterprises
                ?.split(',')
                .map((enterprise) => enterprise.trim())
                .every(
                    (enterprise) => /^E\d{6}$/.test(enterprise) || enterprise === ''
                ) ?? true;


        if (campaignItem?.targetEnterprises && !isValid) {
            setShowDialogToast(true);
            setDialogToastContent(t('campaigns.toastInvalidEnterprise'));
            return false;
        }

        const isStoreValid =
            campaignItem?.targetStores
                ?.split(',')
                .map((store) => store.trim())
                .every(
                    (store) => /^S\d{9}$/.test(store) || store === ''
                ) ?? true;
        if (campaignItem?.targetStores && !isStoreValid) {
            setShowDialogToast(true);
            setDialogToastContent(t('campaigns.toastInvalidStore'));
            return false;
        }

        setShowDialogToast(false);
        return createCampaign();
    }

    const handleUpdatePromotion = () => {
        if (!campaignItem.name?.enUS) {
            setShowDialogToast(true);
            setDialogToastContent(t('campaigns.toastInvalidName'));
            return;
        }
        const urlRegex = /^(http|https):\/\/[^ "]+$/;
        if(campaignItem.link){
        const isLinkValid = urlRegex.test(campaignItem.link);
        if (!isLinkValid) {
            setShowDialogToast(true);
            setDialogToastContent(t('campaigns.toastInvalidLink'));
            return;
        }}
        if (!campaignItem.description?.enUS) {
            setShowDialogToast(true);
            setDialogToastContent(t('campaigns.toastInvalidDescription'));
            return;
        }
        if (campaignItem.showLogo && !campaignItem.logo && !campaignItem.darkLogo) {
            setShowDialogToast(true);
            setDialogToastContent(t('campaigns.toastInvalidLogo'));
            return;
        }
        const isValid =
            campaignItem?.targetEnterprises
                ?.split(',')
                .map((enterprise) => enterprise.trim())
                .every(
                    (enterprise) => /^E\d{6}$/.test(enterprise) || enterprise === ''
                ) ?? true;


        if (campaignItem?.targetEnterprises && !isValid) {
            setShowDialogToast(true);
            setDialogToastContent(t('campaigns.toastInvalidEnterprise'));
            return false;
        }

        const isStoreValid =
            campaignItem?.targetStores
                ?.split(',')
                .map((store) => store.trim())
                .every(
                    (store) => /^S\d{9}$/.test(store) || store === ''
                ) ?? true;
        if (campaignItem?.targetStores && !isStoreValid) {
            setShowDialogToast(true);
            setDialogToastContent(t('campaigns.toastInvalidStore'));
            return false;
        }
        setShowDialogToast(false);
        return updateCampaign();
    }

    const handleCampaignArchival = () => archiveCampaign();

    const handleCloseDialog = () =>{
        setShowDialogToast(false);
        setIsCreateCampaignDialogOpen(false)
        resetCampaignItem(null)
    }

    const [buttonProps, setButtonProps] = useState<ButtonProps[]>([]);
    const [leftButtonProps, setLeftButtonProps] = useState<any>("");
    useEffect(() => {
        let buttonProps: ButtonProps[] = [];
        let leftButtonProps: any = "";
        buttonProps.push({
            id: 'cancel-campaign',
            onClick: () => handleCloseDialog(),
            text: t('campaigns.cancel'),
            variant: 'text'
        });
        
        if (!archiveSwitch) {
            if (campaignItem.id && (permissions?.hasCampaignUpdatePermissions || permissions?.hasCampaignSuperAdminPermissions)) {
                buttonProps.push({
                    id: 'create-campaign',
                    onClick: () => handleUpdatePromotion(),
                    text: t('campaigns.update'),
                    variant: BUTTON_VARIANTS.PRIMARY,
                });

                if (!campaignItem.isActive) {
                    leftButtonProps = {
                        id: 'archive-campaign',
                        onClick: () => handleCampaignArchival(),
                        text: t('campaigns.archive'),
                        variant: BUTTON_VARIANTS.PRIMARY
                    };
                }
            }
            else if (!campaignItem.id) {
                buttonProps.push({
                    id: 'create-campaign',
                    onClick: () => handleCreatePromotion(),
                    text: t('campaigns.create'),
                    variant: BUTTON_VARIANTS.PRIMARY,
                });
            }
        }
        setLeftButtonProps(leftButtonProps);
        setButtonProps(buttonProps);
        // eslint-disable-next-line
    }, [campaignItem, archiveSwitch]);

    return <StyledComponents.StyledCreateCampaignDialog data-testid='create-campaign-dialog-container'>
        <StyledComponents.StyledCreateDialog
            id='create-campaign-dialog'
            buttonsProps={buttonProps}
            leftButtonProps={leftButtonProps}
            dataTestId="create-campaign-dialog"
            onClose={() => handleCloseDialog()}
            title={<><CreatePromotionTitle/></>}
            isOpen={isCreateCampaignDialogOpen}
            disableOverlayClicked>
            {showDialogToast && (
                <AdminStyle.StyledToast
                    variant={'negative'}
                    position="fixed"
                    content={dialogToastContent}
                    visibilityDuration={6000}
                    onVisibilityDurationEnd={() => setShowDialogToast(false)}
                />
            )}
            <CreateCampaignDialogBody/>
        </StyledComponents.StyledCreateDialog>
    </StyledComponents.StyledCreateCampaignDialog>
}